import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Menu, X, Phone, Mail, Linkedin, MessageCircle, Youtube } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { companyInfo } from '../../data/company'
import { emailUtils } from '../../utils/contactUtils'
import LanguageSwitcher from '../LanguageSwitcher/LanguageSwitcher'

const Header: React.FC = () => {
  const { t } = useTranslation()
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)
  const [showWhatsAppQR, setShowWhatsAppQR] = useState(false)

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50)
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // 国际化的导航项
  const getNavigationItems = () => [
    { label: t('navigation.home'), href: '/' },
    { label: t('navigation.products'), href: '/products' },
    { label: t('navigation.solutions'), href: '/solutions' },
    { label: t('navigation.cases'), href: '/cases' },
    { label: t('navigation.news'), href: '/news' },
    { label: t('navigation.about'), href: '/about' }
  ]

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled ? 'bg-white/95 backdrop-blur-md shadow-lg' : 'bg-white'
    }`}>
      {/* Top contact bar */}
      <div className="bg-primary-600 text-white py-3 hidden lg:block">
        <div className="container mx-auto px-6" style={{maxWidth: '150%'}}>
          <div className="flex justify-between items-center text-base">
            <div className="flex items-center space-x-8">
              <div className="flex items-center space-x-3">
                <Phone size={20} />
                <span className="text-lg">{companyInfo.phone}</span>
              </div>
              <div className="flex items-center space-x-3">
                <Mail size={20} />
                <button 
                  onClick={() => emailUtils.openDirectEmail(companyInfo.email)}
                  className="text-lg hover:text-white/80 transition-colors duration-200 cursor-pointer"
                  title={`发送邮件到 ${companyInfo.email}`}
                >
                  {companyInfo.email}
                </button>
              </div>
              
              {/* Social Media Icons */}
              <div className="flex items-center space-x-4 ml-8">
                <a
                  href="https://www.linkedin.com/in/gordon-ye-300263373"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group bg-blue-600 hover:bg-blue-700 p-2.5 rounded-full transition-all duration-300 hover:scale-110 shadow-lg hover:shadow-xl border-2 border-white"
                  aria-label={t('footer.social.linkedin')}
                >
                  <Linkedin size={18} className="text-white" />
                </a>
                <button
                  onClick={() => setShowWhatsAppQR(true)}
                  className="group bg-green-500 hover:bg-green-600 p-2.5 rounded-full transition-all duration-300 hover:scale-110 shadow-lg hover:shadow-xl border-2 border-white"
                  aria-label={t('footer.social.whatsapp')}
                >
                  <MessageCircle size={18} className="text-white" />
                </button>
                <a
                  href="https://youtube.com/@gordonye-e3v?si=5JrKOq-kD_Xt0sTS"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group bg-red-600 hover:bg-red-700 p-2.5 rounded-full transition-all duration-300 hover:scale-110 shadow-lg hover:shadow-xl border-2 border-white"
                  aria-label={t('footer.social.youtube')}
                >
                  <Youtube size={18} className="text-white" />
                </a>
              </div>
            </div>
            <div className="text-lg font-medium">
              {t('hero.subtitle')}
            </div>
          </div>
        </div>
      </div>

      {/* Main navigation */}
      <nav className="py-4">
        <div className="container-custom">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link 
              to="/"
              className="flex items-center space-x-3"
            >
              <div className="w-10 h-10 flex items-center justify-center">
                <img 
                  src="/images/logo/1212.jpg" 
                  alt="MiJin Technology Logo"
                  className="w-full h-full object-contain"
                />
              </div>
              <div>
                <h1 className="text-xl font-bold text-secondary-900">{t('footer.company.name')}</h1>
                <p className="text-sm text-secondary-600">{t('hero.title')}</p>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-8">
              {getNavigationItems().map((item) => (
                              <Link
                  key={item.label}
                      to={item.href}
                      className="py-2 text-secondary-700 hover:text-primary-600 transition-colors duration-200"
                    >
                      {item.label}
                    </Link>
              ))}
              
              {/* 语言切换器 */}
              <LanguageSwitcher />
              
              <button
                className="btn-primary ml-4"
                onClick={() => emailUtils.openConsultationEmail(t)}
              >
                {t('common.getQuote')}
              </button>
            </div>

            {/* Mobile menu button */}
            <button
              className="lg:hidden p-2 text-secondary-700 hover:text-primary-600"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Navigation */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden bg-white border-t border-secondary-200"
          >
            <div className="container-custom py-4">
              <div className="space-y-4">
                {getNavigationItems().map((item) => (
                              <Link
                    key={item.label}
                        to={item.href}
                        className="block w-full text-left py-2 text-secondary-700"
                        onClick={() => setIsMenuOpen(false)}
                      >
                        {item.label}
                      </Link>
                ))}
                
                {/* 移动端语言切换器 */}
                <div className="py-4 border-t border-secondary-200">
                  <div className="text-sm font-medium text-secondary-600 mb-3">
                    {t('common.selectLanguage', '选择语言')}
                  </div>
                  <LanguageSwitcher />
                </div>
                
                <button
                  className="btn-primary w-full justify-center mt-4"
                  onClick={() => emailUtils.openConsultationEmail(t)}
                >
                  {t('common.getQuote')}
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* WhatsApp QR Code Modal */}
      <AnimatePresence>
        {showWhatsAppQR && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
            onClick={() => setShowWhatsAppQR(false)}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              className="bg-white rounded-lg p-6 max-w-sm mx-4 relative"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setShowWhatsAppQR(false)}
                className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
              >
                <X size={24} />
              </button>
              
              <div className="text-center">
                <h3 className="text-lg font-semibold mb-4 text-gray-900">
                  {t('footer.social.qrTitle')}
                </h3>
                <div className="mb-4">
                  <img 
                    src="/images/1123.png" 
                    alt="WhatsApp QR Code"
                    className="w-48 h-48 mx-auto object-contain"
                  />
                </div>
                <p className="text-sm text-gray-600">
                  {t('footer.social.qrDescription')}
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  )
}

export default Header 